/**
 * Orders + shipping addresses service (shop.orders, shop.order_lines,
 * shop.order_shipping_addresses, shop.order_shipments).
 *
 * Buyers see their own orders; vendor team sees orders addressed to
 * them; admins see everything.
 */

import { supabase } from '../client';
import {
  applyKeyset,
  buildNextCursor,
  clampPageSize,
  selectCountOption,
  type KeysetInput,
  type ListResult,
} from '../pagination';
import type {
  ShippingShipMode,
  ShopOrder,
  ShopOrderLine,
  ShopOrderShipment,
  ShopOrderShipmentLine,
  ShopOrderShipmentLotAllocation,
  ShopOrderShippingAddress,
  ShopOrderStatus,
  ShopShipmentCarrier,
} from './shopTypes';

export interface ListShopOrdersInput extends KeysetInput {
  vendorId?: string;
  buyerUserId?: string;
  status?: ShopOrderStatus;
}

export async function listShopOrders(
  input: ListShopOrdersInput,
): Promise<ListResult<ShopOrder>> {
  const pageSize = clampPageSize(input.pageSize);
  let q = (supabase() as any)
    .schema('shop')
    .from('orders')
    .select('*', selectCountOption(input.countMode))
    .is('archived_at', null);

  if (input.vendorId) q = q.eq('vendor_id', input.vendorId);
  if (input.buyerUserId) q = q.eq('buyer_user_id', input.buyerUserId);
  if (input.status) q = q.eq('status', input.status);

  q = applyKeyset(q, input.cursor, 'updated_at');
  q = q.limit(pageSize);

  const { data, error, count } = await q;
  if (error) throw error;
  const rows = (data ?? []) as ShopOrder[];
  return {
    rows,
    nextCursor: buildNextCursor(
      rows as unknown as Array<Record<string, unknown>>,
      pageSize,
      'updated_at',
    ),
    total: typeof count === 'number' ? count : null,
    totalIsExact: input.countMode === 'exact',
  };
}

export async function getShopOrder(id: string): Promise<ShopOrder | null> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .from('orders')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return (data ?? null) as ShopOrder | null;
}

export async function listShopOrderLines(orderId: string): Promise<ShopOrderLine[]> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .from('order_lines')
    .select('*')
    .eq('order_id', orderId)
    .order('sort_order', { ascending: true })
    .order('id', { ascending: true })
    .limit(500);
  if (error) throw error;
  return (data ?? []) as ShopOrderLine[];
}

export async function listShopOrderShipments(orderId: string): Promise<ShopOrderShipment[]> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .from('order_shipments')
    .select('*')
    .eq('order_id', orderId)
    .is('archived_at', null)
    .order('shipped_at', { ascending: false })
    .order('id', { ascending: false })
    .limit(200);
  if (error) throw error;
  return (data ?? []) as ShopOrderShipment[];
}

export async function listShopShipmentLines(shipmentId: string): Promise<ShopOrderShipmentLine[]> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .from('order_shipment_lines')
    .select('*')
    .eq('shipment_id', shipmentId)
    .limit(500);
  if (error) throw error;
  return (data ?? []) as ShopOrderShipmentLine[];
}

export async function listShopShipmentLotAllocations(
  shipmentLineIds: string[],
): Promise<ShopOrderShipmentLotAllocation[]> {
  if (shipmentLineIds.length === 0) return [];
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .from('order_shipment_lot_allocations')
    .select('*')
    .in('shipment_line_id', shipmentLineIds)
    .limit(2000);
  if (error) throw error;
  return (data ?? []) as ShopOrderShipmentLotAllocation[];
}

// ---- shipping addresses -----------------------------------------------

export async function listShopShippingAddresses(): Promise<ShopOrderShippingAddress[]> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .from('order_shipping_addresses')
    .select('*')
    .is('archived_at', null)
    .order('is_default', { ascending: false })
    .order('created_at', { ascending: false })
    .limit(100);
  if (error) throw error;
  return (data ?? []) as ShopOrderShippingAddress[];
}

export interface UpsertShopShippingAddressInput {
  id?: string;
  label?: string | null;
  recipientName: string;
  company?: string | null;
  line1: string;
  line2?: string | null;
  city: string;
  region?: string | null;
  postalCode: string;
  countryIso2: string;
  phone?: string | null;
  notes?: string | null;
  isDefault?: boolean;
}

export async function upsertShopShippingAddress(
  input: UpsertShopShippingAddressInput & { buyerUserId: string },
): Promise<ShopOrderShippingAddress> {
  const row: Partial<ShopOrderShippingAddress> = {
    buyer_user_id: input.buyerUserId,
    label: input.label ?? null,
    recipient_name: input.recipientName,
    company: input.company ?? null,
    line1: input.line1,
    line2: input.line2 ?? null,
    city: input.city,
    region: input.region ?? null,
    postal_code: input.postalCode,
    country_iso2: input.countryIso2,
    phone: input.phone ?? null,
    notes: input.notes ?? null,
    is_default: input.isDefault ?? false,
  };
  const q = (supabase() as any).schema('shop').from('order_shipping_addresses');
  const { data, error } = input.id
    ? await q.update(row).eq('id', input.id).select('*').single()
    : await q.insert(row).select('*').single();
  if (error) throw error;
  return data as ShopOrderShippingAddress;
}

export async function archiveShopShippingAddress(id: string): Promise<void> {
  const { error } = await (supabase() as any)
    .schema('shop')
    .from('order_shipping_addresses')
    .update({ archived_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

// ---- RPCs ---------------------------------------------------------------

export async function buyerAttachShippingToPo(input: {
  poId: string;
  addressId: string;
}): Promise<string> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_buyer_attach_shipping', {
      p_po_id: input.poId,
      p_address_id: input.addressId,
    });
  if (error) throw error;
  return data as string;
}

export async function buyerConfirmOrderReceived(orderId: string): Promise<void> {
  const { error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_buyer_confirm_received', { p_order_id: orderId });
  if (error) throw error;
}

export async function cancelShopOrder(input: {
  orderId: string;
  reason?: string;
}): Promise<void> {
  const { error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_cancel_order', {
      p_order_id: input.orderId,
      p_reason: input.reason ?? null,
    });
  if (error) throw error;
}

export interface VendorCreateShipmentInput {
  orderId: string;
  trackingNumber: string;
  /**
   * Physical carrier code from `shipping.carriers.code` (e.g. `ups`,
   * `usps`, `fedex`, ..., or `other` for a custom carrier). Required.
   */
  carrierCode: string;
  /**
   * Custom carrier name. Required when `carrierCode === 'other'`;
   * ignored otherwise (the RPC auto-fills the canonical display name).
   */
  carrierName?: string;
  /** Mode of transport. Required. */
  shipMode: ShippingShipMode;
  /**
   * Shippo carrier account hint. Defaults to `'shippo'` on the
   * server. This is the Shippo-platform account identifier, NOT the
   * physical carrier; that lives on `carrierCode`.
   */
  carrier?: ShopShipmentCarrier;
  trackingUrl?: string;
  shippoTrackerId?: string;
  notes?: string;
  lines: Array<{
    orderLineId: string;
    qty: number;
    lotAllocations: Array<{ lotId: string; qty: number }>;
  }>;
}

/**
 * Active admin-authored banner notes that the calling party (vendor team
 * member OR buyer-org member) is allowed to see for a given PO or shop
 * order.
 *
 * Backed by `shop.rpc_get_active_order_notes_for_party` (security INVOKER;
 * the underlying SELECT goes through the vendor_select / buyer_select RLS
 * policies on `shop.order_admin_notes`, so the visibility scope is
 * enforced at the database layer -- not in this client wrapper).
 *
 * Returns at most 50 rows. Each row is rendered as a banner above the
 * order content on `apps/sell/src/pages/OrderDetailPage.tsx` and
 * `apps/shop/src/pages/OrderDetailPage.tsx` via the shared
 * `<OrderAdminBanner>` component from `@pc/shop-ui`.
 */
export interface PartyOrderAdminNote {
  id: string;
  severity: 'info' | 'notice' | 'warning';
  visibility: 'internal' | 'vendor_only' | 'buyer_only' | 'both';
  body: string;
  created_at: string;
  dismissible_by_parties: boolean;
}

export async function getActiveOrderNotesForParty(
  targetId: string,
): Promise<PartyOrderAdminNote[]> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_get_active_order_notes_for_party', { p_target_id: targetId });
  if (error) throw error;
  return (data ?? []) as PartyOrderAdminNote[];
}

export async function vendorCreateShipment(input: VendorCreateShipmentInput): Promise<string> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_vendor_create_shipment', {
      p_order_id: input.orderId,
      p_tracking_number: input.trackingNumber,
      p_carrier_code: input.carrierCode,
      p_ship_mode: input.shipMode,
      p_carrier_name: input.carrierName ?? null,
      p_carrier: input.carrier ?? 'shippo',
      p_lines: input.lines.map((l) => ({
        order_line_id: l.orderLineId,
        qty: l.qty,
        lot_allocations: l.lotAllocations.map((a) => ({ lot_id: a.lotId, qty: a.qty })),
      })),
      p_shippo_tracker_id: input.shippoTrackerId ?? null,
      p_tracking_url: input.trackingUrl ?? null,
      p_notes: input.notes ?? null,
    });
  if (error) throw error;
  return data as string;
}
